import {
  CSRF_HEADER,
  type EditAgentReplyRequest,
  type ResolveAnsweredResult,
  WIKI_RESOLUTION_PART,
  type WikiAgentReplyView,
  type WikiResolutionCounts,
  type WikiResolutionRun,
  type WikiThread,
} from "@esi/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { api } from "../api.ts";
import { noContent, ok } from "../api-errors.ts";
import { t as i18nT } from "../i18n/i18n.ts";
import { useSession } from "../session.tsx";
import { wikiKeys } from "./queries.ts";
import { threadKeys } from "./threads.ts";

// Resolve with the agent (openspec change `wiki-resolve-with-agent`, #384): the creator's run of
// one turn over the reviewers' open threads, its drafted replies and the reviewers' batch resolve.

/**
 * The client's own marker on the optimistic user message of a run's turn (never sent: the
 * transport sends only the run's target). The stored message carries the server's
 * `data-wiki-resolution` part instead.
 */
export const RESOLUTION_REQUEST_DATA = "wiki-resolution-request";
export const RESOLUTION_REQUEST_PART = `data-${RESOLUTION_REQUEST_DATA}` as const;
export interface ResolutionRequestData {
  changeSetId: string;
  title: string;
  threadCount: number;
}

/** The stored run part's data (`data-wiki-resolution`, D2). */
interface StoredResolution {
  runId: string;
  changeSetId: string;
  revision: number;
  threadCount: number;
}

/**
 * A resolution run's user message (D2): the server's stored part, or the client's marker on the
 * optimistic message before the stored one replaces it. Null for any other message.
 */
export function resolutionOf(
  message: { role: string; parts: readonly { type: string; data?: unknown }[] } | undefined,
): {
  changeSetId: string;
  runId: string | null;
  revision: number | null;
  threadCount: number;
  title?: string;
} | null {
  if (message?.role !== "user") {
    return null;
  }
  const stored = message.parts.find((p) => p.type === WIKI_RESOLUTION_PART)?.data as
    | StoredResolution
    | undefined;
  if (stored) {
    return { ...stored };
  }
  const pending = message.parts.find((p) => p.type === RESOLUTION_REQUEST_PART)?.data as
    | ResolutionRequestData
    | undefined;
  return pending
    ? {
        changeSetId: pending.changeSetId,
        runId: null,
        revision: null,
        threadCount: pending.threadCount,
        title: pending.title,
      }
    : null;
}

export const resolutionKeys = {
  run: (projectId: string, runId: string) =>
    [...wikiKeys.all(projectId), "resolution-run", runId] as const,
};

/** A run as its creator reviews it (D4); `null` id reads nothing. */
export function useResolutionRun(projectId: string, runId: string | null, poll = false) {
  return useQuery({
    queryKey: resolutionKeys.run(projectId, runId ?? ""),
    enabled: runId !== null,
    retry: false,
    // A turn that ended before the run settled (its end is written after the stream): read again.
    refetchInterval: (query) => (poll && query.state.data?.status === "running" ? 2000 : false),
    queryFn: async (): Promise<WikiResolutionRun> =>
      ok(
        await api.api.projects[":projectId"].wiki["resolution-runs"][":runId"].$get({
          param: { projectId, runId: runId ?? "" },
        }),
      ),
  });
}

export type ResolutionWrite =
  | { kind: "edit"; replyId: string; request: EditAgentReplyRequest }
  | { kind: "send"; replyId: string }
  | { kind: "remove"; replyId: string }
  | { kind: "sendAll" }
  | { kind: "undo"; itemId: string };

/**
 * The creator's decisions on a run's drafts (D4): each refreshes the run, the set (its counts,
 * eligibility and agent revision), its threads and the review queue.
 */
export function useResolutionWrite(projectId: string, runId: string, changeSetId: string) {
  const queryClient = useQueryClient();
  const { csrfToken } = useSession();
  const headers = { [CSRF_HEADER]: csrfToken };
  return useMutation({
    mutationFn: async (write: ResolutionWrite): Promise<void> => {
      const wiki = api.api.projects[":projectId"].wiki;
      if (write.kind === "sendAll") {
        await ok(
          await wiki["resolution-runs"][":runId"]["send-all"].$post(
            { param: { projectId, runId } },
            { headers },
          ),
        );
        return;
      }
      if (write.kind === "undo") {
        const run = await ok(
          await wiki["resolution-runs"][":runId"]["undo-item"].$post(
            { param: { projectId, runId }, json: { itemId: write.itemId } },
            { headers },
          ),
        );
        queryClient.setQueryData(resolutionKeys.run(projectId, runId), run);
        return;
      }
      const param = { projectId, replyId: write.replyId };
      const reply = wiki["agent-replies"][":replyId"];
      if (write.kind === "edit") {
        await noContent(await reply.$patch({ param, json: write.request }, { headers }));
      } else if (write.kind === "send") {
        await ok(await reply.send.$post({ param }, { headers }));
      } else {
        await noContent(await reply.remove.$post({ param }, { headers }));
      }
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: resolutionKeys.run(projectId, runId) });
      void queryClient.invalidateQueries({
        queryKey: [...wikiKeys.all(projectId), "change", changeSetId],
      });
      void queryClient.invalidateQueries({ queryKey: threadKeys.set(projectId, changeSetId) });
      void queryClient.invalidateQueries({ queryKey: wikiKeys.review(projectId) });
    },
  });
}

/** Resolve answered (D5): the server resolves those the caller may, and skips the rest. */
export function useResolveAnswered(projectId: string, changeSetId: string) {
  const queryClient = useQueryClient();
  const { csrfToken } = useSession();
  return useMutation({
    mutationFn: async (input: {
      threadIds: string[];
      note?: string | undefined;
    }): Promise<ResolveAnsweredResult> =>
      ok(
        await api.api.projects[":projectId"].wiki["change-sets"][":id"].threads[
          "resolve-answered"
        ].$post(
          {
            param: { projectId, id: changeSetId },
            json: { threadIds: input.threadIds, ...(input.note && { note: input.note }) },
          },
          { headers: { [CSRF_HEADER]: csrfToken } },
        ),
      ),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: threadKeys.set(projectId, changeSetId) });
      void queryClient.invalidateQueries({
        queryKey: [...wikiKeys.all(projectId), "change", changeSetId],
      });
      void queryClient.invalidateQueries({ queryKey: wikiKeys.review(projectId) });
    },
  });
}

/** The latest sent reply of a thread, read or hidden, by its sending time. */
export function latestSentReply(
  thread: Pick<WikiThread, "agentReplies">,
): WikiAgentReplyView | null {
  const sent = (thread.agentReplies?.replies ?? []).filter((r) => r.sentAt !== null);
  return sent.reduce<WikiAgentReplyView | null>(
    (latest, r) => (!latest || (r.sentAt ?? "") > (latest.sentAt ?? "") ? r : latest),
    null,
  );
}

/**
 * The threads Resolve answered (N) counts for the viewer (D5): open, theirs to resolve, and whose
 * latest word is a sent Changed or No change reply (a hidden reply counts: the thread is
 * answered). The server applies the same rule and skips anything else.
 */
export function answeredThreads(threads: readonly WikiThread[]): WikiThread[] {
  return threads.filter((thread) => {
    if (thread.status !== "open" || !thread.mayResolve) {
      return false;
    }
    const reply = latestSentReply(thread);
    if (!reply || (!reply.hidden && reply.outcome === "question")) {
      return false;
    }
    if (reply.hidden && thread.agentReplies?.waitingFor) {
      // A hidden question still waits on its reviewer.
      return false;
    }
    const lastComment =
      thread.comments
        .filter((c) => !c.system)
        .map((c) => c.createdAt)
        .sort()
        .at(-1) ?? "";
    return (reply.sentAt ?? "") > lastComment;
  });
}

/** "2 changed · 1 question · 1 no change", omitting zeros; "no reply drafted" when all are. */
export function countsText(
  counts: Pick<WikiResolutionCounts, "changed" | "question" | "noChange">,
) {
  const t = i18nT;
  const words = [
    ...(counts.changed ? [t("wiki.resolve.counts.changed", { count: counts.changed })] : []),
    ...(counts.question ? [t("wiki.resolve.counts.question", { count: counts.question })] : []),
    ...(counts.noChange ? [t("wiki.resolve.counts.noChange", { count: counts.noChange })] : []),
  ];
  return words.length ? words.join(" · ") : t("wiki.resolve.none");
}

// The hand-off of a trigger outside the thread (the preview) to the conversation's thread, as
// the instructions tray does: kept in `sessionStorage` until the thread is free to send it.
const resolveKey = (conversationId: string) => `esi.wiki.resolve.send.${conversationId}`;
const RESOLVE_EVENT = "esi:wiki-resolve";
const held = new Map<string, ResolutionRequestData>();

function readQueued(conversationId: string): ResolutionRequestData | null {
  try {
    const raw = window.sessionStorage.getItem(resolveKey(conversationId));
    const value = raw ? (JSON.parse(raw) as Partial<ResolutionRequestData>) : null;
    return value && typeof value.changeSetId === "string" && typeof value.threadCount === "number"
      ? {
          changeSetId: value.changeSetId,
          title: String(value.title ?? ""),
          threadCount: value.threadCount,
        }
      : null;
  } catch {
    return held.get(conversationId) ?? null;
  }
}

function clearQueued(conversationId: string) {
  held.delete(conversationId);
  try {
    window.sessionStorage.removeItem(resolveKey(conversationId));
  } catch {
    // Held in memory only.
  }
}

/** Asks the set's conversation to start a run as its next turn (one pending at a time). */
export function queueResolve(conversationId: string, request: ResolutionRequestData) {
  // One pending run per conversation: a second click before the first is sent does nothing.
  if (readQueued(conversationId) ?? held.get(conversationId)) {
    return;
  }
  held.set(conversationId, request);
  try {
    window.sessionStorage.setItem(resolveKey(conversationId), JSON.stringify(request));
  } catch {
    // Held in memory only.
  }
  window.dispatchEvent(new CustomEvent(RESOLVE_EVENT, { detail: conversationId }));
}

/** The thread's side: start the queued run for `conversationId` whenever `ready`. */
export function useQueuedResolve(
  conversationId: string | undefined,
  ready: boolean,
  send: (request: ResolutionRequestData) => void,
) {
  useEffect(() => {
    if (!conversationId || !ready) {
      return;
    }
    const take = () => {
      const next = readQueued(conversationId) ?? held.get(conversationId) ?? null;
      if (next) {
        clearQueued(conversationId);
        send(next);
      }
    };
    take();
    const onEvent = (e: Event) => {
      if ((e as CustomEvent<string>).detail === conversationId) {
        take();
      }
    };
    window.addEventListener(RESOLVE_EVENT, onEvent);
    return () => window.removeEventListener(RESOLVE_EVENT, onEvent);
  }, [conversationId, ready, send]);
}
